/**
 * O CAMPO DE FOTO DO CONSTRUTOR DE SITE.
 *
 * O QUE ELE SUBSTITUI. Todo campo de imagem do construtor era uma caixa de
 * TEXTO pedindo endereço. A ajuda embaixo do campo de Open Graph dizia isso com
 * todas as letras: "Por enquanto é o endereço de uma imagem que já esteja na
 * internet — o envio de foto pelo editor ainda não existe". Para pôr a foto da
 * fachada na capa, o dono do restaurante precisava primeiro hospedar o arquivo
 * em outro lugar e voltar com o link.
 *
 * O caminho do arquivo JÁ EXISTIA — `POST /midia`, autenticado, gravando no R2
 * com a chave só do lado do servidor (`midia.service.ts`). Faltava a peça de
 * tela. É esta.
 *
 * A CAIXA DE ENDEREÇO CONTINUA, e de propósito: quem já tem a foto publicada
 * em outro lugar (o site antigo, o Instagram, um Drive) não precisa baixar e
 * subir de novo. Enviar é o caminho fácil, colar continua sendo um caminho.
 *
 * POR QUE O VALOR GRAVADO É UMA URL ABSOLUTA. `midiaService.enviar` devolve
 * `https://<api>/midia/<escopo>/<uuid>.<ext>`, e é isso que fica no bloco. Duas
 * razões, e a segunda é a que manda:
 *
 *  1. `urlDeImagem()` do catálogo compartilhado só aceita http/https absoluto —
 *     um caminho relativo é recusado na gravação;
 *  2. quem RENDERIZA o site publicado é o alfredo-pedidos, em OUTRA origem
 *     (`<handle>.alfredo.food`). Um caminho relativo ali apontaria para o
 *     próprio site do restaurante, onde a rota não existe.
 *
 * O preço disso é uma amarra: o host da API fica escrito dentro do dado do
 * site. Se um dia o backend trocar de endereço, as fotos dos blocos param — e
 * o conserto é uma migração no banco, não um redeploy. Fica registrado aqui
 * porque é o tipo de coisa que ninguém lembra na hora.
 */
import React, { useRef, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Site";
import midiaService, { EscopoDeMidia } from "../../../../services/midia.service";

/** 8 MB — o mesmo teto que o servidor aplica em `r2.service.ts`. */
const TETO_BYTES = 8 * 1024 * 1024;

/**
 * Os tipos que o servidor aceita. Repetidos aqui NÃO para validar de verdade —
 * quem valida é o backend — mas para o seletor de arquivo do sistema já abrir
 * filtrado, e para a recusa acontecer antes de gastar o upload.
 */
const TIPOS = "image/jpeg,image/png,image/webp,image/gif";

interface Props {
  rotulo: string;
  ajuda?: React.ReactNode;
  valor: string;
  aoMudar: (url: string) => void;
  /** A pasta no R2. O construtor de site usa `company`. */
  escopo?: EscopoDeMidia;
  desabilitado?: boolean;
}

export const CampoDeImagem: React.FC<Props> = ({
  rotulo,
  ajuda,
  valor,
  aoMudar,
  escopo = "company",
  desabilitado = false,
}) => {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const refDoArquivo = useRef<HTMLInputElement | null>(null);

  const enviar = async (arquivo: File) => {
    setErro(null);

    if (arquivo.size > TETO_BYTES) {
      setErro(
        `A foto tem ${(arquivo.size / 1024 / 1024).toFixed(1)} MB e o limite é ` +
          `${TETO_BYTES / 1024 / 1024} MB. Reduza a imagem e tente de novo.`,
      );
      return;
    }

    setEnviando(true);
    try {
      const url = await midiaService.enviar(arquivo, escopo);
      aoMudar(url);
    } catch (e: any) {
      // A mensagem do servidor é mais útil que "Request failed with status
      // code 400" — ela diz QUAL tipo não passou, ou que o arquivo é grande.
      setErro(
        e?.response?.data?.message ||
          e?.message ||
          "Não consegui enviar a foto. Tente de novo.",
      );
    } finally {
      setEnviando(false);
      // Zerar o input para que escolher O MESMO arquivo outra vez volte a
      // disparar o `change` — sem isso, corrigir um envio que falhou exige
      // escolher outro arquivo antes.
      if (refDoArquivo.current) refDoArquivo.current.value = "";
    }
  };

  return (
    <AlfredoStyles.Campo as="div">
      <span>{rotulo}</span>

      <AlfredoStyles.ImagemLinha>
        <AlfredoStyles.ImagemMiniatura>
          {valor ? (
            // `alt` vazio e `aria-hidden`: a miniatura é a PRÉVIA do que o
            // campo ao lado já diz. Anunciá-la de novo seria repetição, e uma
            // descrição inventada ("imagem") não informa nada.
            <img src={valor} alt="" aria-hidden="true" />
          ) : (
            <AlfredoStyles.MaterialIcons aria-hidden="true">
              image
            </AlfredoStyles.MaterialIcons>
          )}
        </AlfredoStyles.ImagemMiniatura>

        <AlfredoStyles.ImagemControles>
          <AlfredoStyles.Entrada
            placeholder="https://…"
            value={valor}
            disabled={desabilitado || enviando}
            aria-label={`${rotulo} — endereço da imagem`}
            onChange={(e) => {
              setErro(null);
              aoMudar(e.target.value);
            }}
          />

          <AlfredoStyles.ImagemAcoes>
            <AlfredoStyles.ImagemRotulo>
              <AlfredoStyles.ImagemEntradaArquivo
                ref={refDoArquivo}
                type="file"
                accept={TIPOS}
                disabled={desabilitado || enviando}
                onChange={(e) => {
                  const arquivo = e.target.files?.[0];
                  if (arquivo) void enviar(arquivo);
                }}
              />
              <AlfredoStyles.ImagemBotao>
                <AlfredoStyles.MaterialIcons aria-hidden="true">
                  {enviando ? "hourglass_top" : "upload"}
                </AlfredoStyles.MaterialIcons>
                {enviando ? "Enviando…" : valor ? "Trocar foto" : "Enviar foto"}
              </AlfredoStyles.ImagemBotao>
            </AlfredoStyles.ImagemRotulo>

            {valor && !enviando && (
              <AlfredoStyles.ImagemBotao
                as="button"
                type="button"
                disabled={desabilitado}
                onClick={() => {
                  setErro(null);
                  aoMudar("");
                }}
              >
                <AlfredoStyles.MaterialIcons aria-hidden="true">
                  delete
                </AlfredoStyles.MaterialIcons>
                Remover
              </AlfredoStyles.ImagemBotao>
            )}
          </AlfredoStyles.ImagemAcoes>

          {/*
            `role="alert"` porque o erro aparece DEPOIS de uma ação, longe de
            onde o olho está: sem ele, quem usa leitor de tela envia um arquivo
            grande e não fica sabendo que foi recusado.
          */}
          {erro && (
            <AlfredoStyles.Aviso $tom="erro" role="alert">
              {erro}
            </AlfredoStyles.Aviso>
          )}
        </AlfredoStyles.ImagemControles>
      </AlfredoStyles.ImagemLinha>

      {ajuda && <AlfredoStyles.CampoAjuda>{ajuda}</AlfredoStyles.CampoAjuda>}
    </AlfredoStyles.Campo>
  );
};

export default CampoDeImagem;
